import apiClient from './client';
import { API_HEADERS_GET, API_HEADERS_POST, API_HEADERS_DELETE } from './headers';
import {
  API_GET_RIGS_ENDPOINT,
  API_GET_WELLS_ENDPOINT,
  API_GET_INFERENCE_RESULTS_ENDPOINT,
  API_WELL_RETORT_HISTORY_ENDPOINT,
  createApiGetCalibrationEndpoint,
  createApiPutCalibrationEndpoint,
  createApiDeleteRetortHistoryEndpoint,
} from './config';
import { Calibration, InferenceResult, RetortHistory, Rig, Well } from '@/models/models';

export async function fetchRigs(): Promise<Rig[]> {
  const response = await apiClient.get<Rig[]>(API_GET_RIGS_ENDPOINT, {
    headers: API_HEADERS_GET,
  });
  return response.data;
}

export async function fetchRigById(rigId: number): Promise<Rig | null> {
  const rigs = await fetchRigs();
  return rigs.find((rig) => rig.id === rigId) || null;
}

export async function fetchWells(): Promise<Well[]> {
  const response = await apiClient.get<Well[]>(API_GET_WELLS_ENDPOINT, {
    headers: API_HEADERS_GET,
  });
  return response.data;
}

export async function fetchWellById(wellId: number): Promise<Well | null> {
  const wells = await fetchWells();
  return wells.find((w) => w.id === wellId) || null;
}

export async function fetchWellsByRigId(rigId: number): Promise<Well[]> {
  const wells = await fetchWells();
  return wells.filter((well) => well.rig_id === rigId);
}

export async function fetchCalibration(rigId: number): Promise<Calibration> {
  const response = await apiClient.get<Calibration>(
    createApiGetCalibrationEndpoint(rigId),
    {
      headers: API_HEADERS_GET,
    }
  );
  return response.data;
}

export async function saveCalibration(
  rigId: number,
  calibrationData: Omit<Calibration, 'rig_id' | 'created_at' | 'updated_at'>
): Promise<void> {
  await apiClient.post(
    createApiPutCalibrationEndpoint(rigId),
    calibrationData,
    {
      headers: API_HEADERS_POST,
    }
  );
}

export async function fetchRetortHistory(wellId: number): Promise<RetortHistory[]> {
  const response = await apiClient.get<RetortHistory[]>(
    API_WELL_RETORT_HISTORY_ENDPOINT,
    {
      params: { well_id: wellId },
      headers: API_HEADERS_GET,
    }
  );
  return response.data.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

export async function addRetortHistory(
  wellId: number,
  mudDensity: number,
  oilPercentage: number,
  waterPercentage: number,
  sampleSource?: string | null
): Promise<RetortHistory[]> {
  await apiClient.post(
    API_WELL_RETORT_HISTORY_ENDPOINT,
    {
      well_id: wellId,
      mud_density: mudDensity,
      oil_percentage: oilPercentage,
      water_percentage: waterPercentage,
      sample_source: sampleSource,
    },
    {
      headers: API_HEADERS_POST,
    }
  );
  return fetchRetortHistory(wellId);
}

export async function deleteRetortHistory(historyId: number): Promise<void> {
  await apiClient.delete(createApiDeleteRetortHistoryEndpoint(historyId), {
    headers: API_HEADERS_DELETE,
  });
}

export async function fetchInferenceResultsByRig(rigName: string): Promise<{ data: InferenceResult[]; total: number }> {
  const response = await apiClient.get<{ data: InferenceResult[]; total: number }>(
    API_GET_INFERENCE_RESULTS_ENDPOINT,
    {
      params: {
        limit: 10000, // Fetch all results for client-side filtering
        offset: 0,
        rig_name: rigName,
      },
      headers: API_HEADERS_GET,
    }
  );
  return response.data;
}
